import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConsignmentInvoices } from '../api/hooks';
import type { ConsignmentInvoiceRow, ConsignmentInvoiceState, ShipmentState } from '../api/types';
import { shipmentStateLabel } from './shipment-state';
import { Alert, Button, Chip, Empty, Money, PageHeader, Panel, Spinner, Table, type Tone } from '../ui';

const SHIPMENT_TONE: Record<ShipmentState, Tone> = {
  DRAFT: 'them', CONFIRMED: 'us', PACKED: 'us', AWAITING_PAYMENT: 'stop', DISPATCHED: 'run', DELIVERED: 'done', CANCELLED: 'lapse',
};

/* A `Record` over the union on purpose, as on the detail screen: SH-14's terminal state (D92) is a
   fourth member and a Record fails typecheck the day it is added. The label is
   `t(`consignmentInvoice.state.${state}`)`, never a map beside this one. */
const STATE_TONE: Record<ConsignmentInvoiceState, Tone> = { OPEN: 'them', SHIPMENT_CREATED: 'us', CANCELLED: 'lapse' };

/**
 * A-18 — the Consignment Invoice list (docs/plans/consignment-invoice-parent §7).
 * Shipments are created inside an invoice, so this replaces the old shipment list.
 */
export function ConsignmentInvoices() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useConsignmentInvoices();
  const navigate = useNavigate();

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  return (
    <>
      <PageHeader
        title="Consignment invoices"
        actions={<Button onClick={() => void navigate('/consignment-invoices/new')}>Create Consignment Invoice</Button>}
      />
      <Panel>
        <Table<ConsignmentInvoiceRow>
          className="mk-pi-list-t"
          rows={data ?? []}
          rowKey={(r) => r.id}
          empty={(
            <Empty
              title="No consignment invoices yet"
              body="Group the orders that are ready into a consignment invoice, then create the shipment from it."
              action={<Button onClick={() => void navigate('/consignment-invoices/new')}>Create Consignment Invoice</Button>}
            />
          )}
          columns={[
            { header: 'Consignment invoice', render: (r) => <Link to={`/consignment-invoices/${r.id}`}>{r.reference}</Link> },
            { header: 'Company', render: (r) => <span title={r.retailer.name}>{r.retailer.name}</span> },
            { header: 'Orders', render: (r) => r.orderReferences.join(', ') },
            // Already on the row and worth a column now that the table is no longer stretched thin.
            { header: 'Created', render: (r) => new Date(r.createdAt).toLocaleDateString() },
            /* What was sent, and nothing else: the goods are consigned, so not a debt (SH-06), and no
               courier charge is payable on a despatch either (client direction, 04-Nov-2026). There
               is NO payable, payment-status or still-to-pay column here — money is settled on the
               purchase invoice, on A-40 Sales & payables. The invoice's own state replaces the old
               "Payment Status" column, which was also the only place Cancelled showed. */
            { header: t('despatch.colConsigned'), numeric: true, render: (r) => <Money amount={r.consignedValue} currency={r.currency} /> },
            { header: 'Status', render: (r) => <Chip tone={STATE_TONE[r.state]}>{t(`consignmentInvoice.state.${r.state}`)}</Chip> },
            {
              header: 'Shipment',
              render: (r) => (r.shipment
                ? (
                  <span>
                    {r.shipment.reference}{' '}
                    <Chip tone={SHIPMENT_TONE[r.shipment.state]}>{shipmentStateLabel(r.shipment.state)}</Chip>
                  </span>
                )
                : <span className="mk-dim">—</span>),
            },
          ]}
        />
      </Panel>
    </>
  );
}

